import { useEffect, useState } from "react";
import * as api from "./api.js";
import readingStatusMarkdown from "../../roray-status.md?raw";

const tabs = [
  ["today", "Today"], ["roadmap", "Roadmap"], ["schedule", "Schedule"], ["resources", "Resources"],
  ["roray-reading", "Roray Reading"], ["settings", "Settings"]
];

export default function App() {
  const [data, setData] = useState(null);
  const [authRequired, setAuthRequired] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(location.hash.slice(1) || "today");
  const [editingTask, setEditingTask] = useState(null);
  const [focusedSprintId, setFocusedSprintId] = useState(null);
  const [notice, setNotice] = useState(null);

  async function refresh() {
    try {
      setData(await api.bootstrap());
      setAuthRequired(false);
    } catch (error) {
      if (error.status === 401) setAuthRequired(true);
      else setNotice({ type: "error", text: error.message });
    } finally { setLoading(false); }
  }

  useEffect(() => { refresh(); }, []);
  useEffect(() => {
    const listener = () => setTab(location.hash.slice(1) || "today");
    addEventListener("hashchange", listener);
    return () => removeEventListener("hashchange", listener);
  }, []);
  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(null), 4500);
    return () => clearTimeout(timer);
  }, [notice]);
  useEffect(() => {
    if (tab !== "roadmap") setFocusedSprintId(null);
  }, [tab]);

  async function mutate(action, success) {
    try {
      await action();
      await refresh();
      if (success) setNotice({ type: "success", text: success });
    } catch (error) {
      if (error.status === 401) setAuthRequired(true);
      else setNotice({ type: "error", text: error.message });
      throw error;
    }
  }

  if (loading) return <Loading />;
  if (authRequired) return <Login onLogin={refresh} />;
  if (!data) return <ErrorState onRetry={refresh} />;

  const allSprints = data.phases.flatMap((phase) => phase.sprints);
  const allTasks = allSprints.flatMap((sprint) => sprint.tasks);
  const currentSprint = allSprints.find((sprint) => data.today >= sprint.startDate && data.today <= sprint.endDate)
    ?? allSprints.find((sprint) => sprint.endDate >= data.today) ?? allSprints.at(-1);
  const editingSprint = editingTask ? allSprints.find((sprint) => sprint.id === editingTask.sprintId) : null;

  function openSprint(sprintId) {
    setEditingTask(null);
    setFocusedSprintId(sprintId);
    setTab("roadmap");
    if (location.hash !== "#roadmap") location.hash = "roadmap";
  }

  return <div className="app-shell">
    <aside className="sidebar">
      <div className="brand">
        <div className="brand-mark">N</div>
        <div><strong>.NET ascent</strong><span>HR SaaS roadmap</span></div>
      </div>
      <nav aria-label="Primary navigation">
        {tabs.map(([id, label]) => <a key={id} className={tab === id ? "active" : ""} href={`#${id}`}>{label}</a>)}
      </nav>
      <div className="sidebar-progress">
        <span>Overall progress</span>
        <strong>{formatPercent(data.stats.progressPercent)}</strong>
        <Progress value={data.stats.progressPercent} />
        <small>{data.stats.doneTasks} of {data.stats.totalTasks} tasks complete</small>
      </div>
      <button className="quiet-button" onClick={() => mutate(api.logout).then(() => setAuthRequired(true))}>Sign out</button>
    </aside>

    <main>
      <header className="topbar">
        <div>
          <p className="eyebrow">18-week architecture-first programme</p>
          <h1>{tabs.find(([id]) => id === tab)?.[1] ?? "Roadmap"}</h1>
        </div>
        <div className="target-chip"><span>Target</span><strong>{prettyDate(data.metadata.targetDate)}</strong></div>
      </header>

      {tab === "today" && <Today data={data} currentSprint={currentSprint} tasks={allTasks} onEdit={setEditingTask} onOpenSprint={openSprint} mutate={mutate} />}
      {tab === "roadmap" && <Roadmap data={data} currentSprint={currentSprint} focusedSprintId={focusedSprintId} onEdit={setEditingTask} />}
      {tab === "schedule" && <Schedule data={data} onEdit={(id) => setEditingTask(allTasks.find((task) => task.id === id))} />}
      {tab === "resources" && <Resources sprints={allSprints} onOpenSprint={openSprint} />}
      {tab === "roray-reading" && <RorayReading />}
      {tab === "settings" && <Settings data={data} mutate={mutate} />}
    </main>

    {editingTask && <TaskEditor task={editingTask} sprint={editingSprint} onOpenSprint={openSprint} onClose={() => setEditingTask(null)} onSave={(update) => mutate(() => api.updateTask(editingTask.id, update), "Task updated").then(() => setEditingTask(null))} />}
    {notice && <div role="status" className={`toast ${notice.type}`}>{notice.text}</div>}
  </div>;
}

function Today({ data, currentSprint, tasks, onEdit, onOpenSprint, mutate }) {
  const plan = data.schedule.find((day) => day.date === data.today) ?? { allocations: [], plannedMinutes: 0, capacityMinutes: 0 };
  const todaysTasks = plan.allocations.map((allocation) => ({ ...allocation, task: tasks.find((task) => task.id === allocation.taskId) }));
  const forecastTone = data.stats.slipDays > 0 ? "warn" : "good";

  return <section className="page-stack">
    <div className="hero-grid">
      <article className="hero-card">
        <p className="eyebrow">{prettyDate(data.today)}</p>
        <h2>{currentSprint ? `Week ${currentSprint.weekNumber}: ${currentSprint.title}` : "Roadmap complete"}</h2>
        <p>{currentSprint?.goal}</p>
        {currentSprint && <button className="context-link" onClick={() => onOpenSprint(currentSprint.id)}>Open Week {currentSprint.weekNumber} plan and resources →</button>}
        <div className="hero-meter"><Progress value={sprintProgress(currentSprint)} /><span>{formatPercent(sprintProgress(currentSprint))} this sprint</span></div>
      </article>
      <article className={`forecast-card ${forecastTone}`}>
        <span>Current forecast</span>
        <strong>{data.stats.forecastDate ? prettyDate(data.stats.forecastDate) : "Complete"}</strong>
        <small>{data.stats.slipDays > 0 ? `${data.stats.slipDays} day${data.stats.slipDays === 1 ? "" : "s"} after target` : data.stats.slipDays < 0 ? `${Math.abs(data.stats.slipDays)} days of buffer` : "On target"}</small>
        {data.stats.overdueTasks > 0 && <button className="catchup-button" onClick={() => mutate(() => api.reschedule(data.today), "Past unfinished work moved from today")}>Replan overdue work</button>}
      </article>
    </div>

    <div className="stats-grid">
      <Stat label="Earned effort" value={formatHours(data.stats.earnedMinutes)} detail={`of ${formatHours(data.stats.estimatedMinutes)}`} />
      <Stat label="Actual effort" value={formatHours(data.stats.actualMinutes)} detail="recorded across tasks" />
      <Stat label="Blocked" value={data.stats.blockedTasks} detail="tasks need attention" />
      <Stat label="Past target" value={data.stats.overdueTasks} detail="unfinished task targets" />
    </div>

    <div className="content-grid">
      <article className="panel task-panel">
        <div className="panel-heading">
          <div><p className="eyebrow">Daily plan</p><h2>{formatHours(plan.plannedMinutes)} planned / {formatHours(plan.capacityMinutes)} capacity</h2></div>
          <span className="count-badge">{todaysTasks.length}</span>
        </div>
        {todaysTasks.length === 0 ? <Empty title="No work allocated today" text="Use Settings to add capacity or reschedule from a chosen date." /> :
          <div className="task-list">{todaysTasks.map(({ task, ...allocation }) => task && <TaskRow key={task.id} task={task} allocation={allocation} onEdit={() => onEdit(task)} onQuick={(update) => mutate(() => api.updateTask(task.id, update), "Progress saved")} />)}</div>}
      </article>
      <CheckinCard today={data.today} existing={data.checkins.find((item) => item.date === data.today)} mutate={mutate} />
    </div>

    <article className="panel focus-panel">
      <p className="eyebrow">Architecture checkpoint</p>
      <h2>{currentSprint?.architectureFocus}</h2>
      <p><strong>Working increment:</strong> {currentSprint?.deliverable}</p>
      <p><strong>Exit evidence:</strong> {currentSprint?.acceptanceCriteria}</p>
    </article>
  </section>;
}

function TaskRow({ task, allocation, onEdit, onQuick }) {
  return <div className={`task-row status-${task.status}`}>
    <button className="task-check" aria-label={`Mark ${task.title} complete`} onClick={() => onQuick({ status: "done", progressPercent: 100 })}>{task.status === "done" ? "✓" : ""}</button>
    <button className="task-copy" onClick={onEdit}>
      <span className="task-meta"><Kind kind={task.kind} /> {formatHours(allocation.allocationMinutes)} today · {formatHours(task.estimatedMinutes)} total</span>
      <strong>{task.title}</strong>
      <span>{task.description}</span>
      <Progress value={task.progressPercent} compact />
    </button>
    {task.status === "todo" && <button className="mini-button" onClick={() => onQuick({ status: "in_progress" })}>Start</button>}
  </div>;
}

function CheckinCard({ today, existing, mutate }) {
  const [actualHours, setActualHours] = useState(existing ? existing.actualMinutes / 60 : 0);
  const [reflection, setReflection] = useState(existing?.reflection ?? "");
  const [blockers, setBlockers] = useState(existing?.blockers ?? "");
  const [energy, setEnergy] = useState(existing?.energy ?? 3);
  const [saving, setSaving] = useState(false);

  async function submit(event) {
    event.preventDefault(); setSaving(true);
    try {
      await mutate(() => api.saveCheckin({ date: today, actualMinutes: Math.round(Number(actualHours) * 60), reflection, blockers, energy: Number(energy) }), "EOD saved; unfinished work was replanned");
    } finally { setSaving(false); }
  }
  return <article className="panel checkin-panel">
    <div className="panel-heading"><div><p className="eyebrow">End-of-day</p><h2>{existing ? "Update check-in" : "Close the loop"}</h2></div>{existing && <span className="saved-badge">Saved</span>}</div>
    <form onSubmit={submit}>
      <label>Actual learning hours<input type="number" min="0" max="24" step="0.25" value={actualHours} onChange={(e) => setActualHours(e.target.value)} /></label>
      <label>What moved forward?<textarea rows="3" value={reflection} onChange={(e) => setReflection(e.target.value)} placeholder="Evidence produced, concepts understood…" /></label>
      <label>Blockers<textarea rows="2" value={blockers} onChange={(e) => setBlockers(e.target.value)} placeholder="Leave blank when none" /></label>
      <label>Energy: {energy}/5<input type="range" min="1" max="5" value={energy} onChange={(e) => setEnergy(e.target.value)} /></label>
      <button className="primary-button" disabled={saving}>{saving ? "Saving…" : "Save EOD and replan"}</button>
      <small>Tasks not completed remain in sequence and are allocated from tomorrow using your capacity calendar.</small>
    </form>
  </article>;
}

function Roadmap({ data, currentSprint, focusedSprintId, onEdit }) {
  useEffect(() => {
    if (!focusedSprintId) return;
    const frame = requestAnimationFrame(() => {
      const sprint = document.getElementById(`sprint-${focusedSprintId}`);
      const summary = sprint?.querySelector("summary");
      sprint?.scrollIntoView({ behavior: "smooth", block: "center" });
      summary?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [focusedSprintId]);

  return <section className="page-stack roadmap-page">
    <article className="panel overview-strip">
      <div><p className="eyebrow">Programme contract</p><h2>{data.metadata.totalHours} hours · {data.metadata.totalWeeks} weeks · target {prettyDate(data.metadata.targetDate)}</h2></div>
      <p>Reading is paired with code, tests, architecture decisions, and a working increment. The app tracks effort-weighted progress.</p>
    </article>
    {data.phases.map((phase) => <section key={phase.id} className="phase-section">
      <div className="phase-heading"><span>{phase.sequence}</span><div><h2>{phase.title}</h2><p>{phase.description}</p></div></div>
      <div className="sprint-list">{phase.sprints.map((sprint) => <details id={`sprint-${sprint.id}`} key={sprint.id} className={`sprint-card ${sprint.id === focusedSprintId ? "focused" : ""}`} open={sprint.id === currentSprint?.id || sprint.id === focusedSprintId}>
        <summary>
          <span className="week-number">W{sprint.weekNumber}</span>
          <span className="summary-copy"><strong>{sprint.title}</strong><small>{prettyDate(sprint.startDate)}–{prettyDate(sprint.endDate)} · {formatHours(sprint.capacityMinutes)}</small></span>
          <span className="summary-progress"><Progress value={sprintProgress(sprint)} compact /><small>{formatPercent(sprintProgress(sprint))}</small></span>
        </summary>
        <div className="sprint-body">
          <div className="sprint-brief"><p><strong>Goal</strong>{sprint.goal}</p><p><strong>Increment</strong>{sprint.deliverable}</p><p><strong>Architecture</strong>{sprint.architectureFocus}</p></div>
          <div className="roadmap-tasks">{sprint.tasks.map((task) => <button key={task.id} className={`roadmap-task status-${task.status}`} onClick={() => onEdit(task)}>
            <span className="status-dot" /><span><strong>{task.title}</strong><small><Kind kind={task.kind} /> {formatHours(task.estimatedMinutes)} · {task.notBefore}{task.notBefore === task.targetDate ? "" : ` → ${task.targetDate}`}</small></span><span>{task.progressPercent}%</span>
          </button>)}</div>
          <SprintLearningPlans sprint={sprint} />
          <div className="exit-box"><strong>Exit criteria</strong><p>{sprint.acceptanceCriteria}</p></div>
        </div>
      </details>)}</div>
    </section>)}
  </section>;
}

function Schedule({ data, onEdit }) {
  return <section className="page-stack">
    <article className="panel overview-strip"><div><p className="eyebrow">Rolling 42-day allocation</p><h2>Capacity-aware plan</h2></div><p>Completed history stays fixed. EOD check-ins and capacity overrides recalculate only future unfinished effort.</p></article>
    <div className="calendar-list">{data.schedule.map((day) => <article key={day.date} className={`day-card ${day.date === data.today ? "today" : ""} ${day.capacityMinutes === 0 ? "rest" : ""}`}>
      <div className="day-heading"><div><span>{weekday(day.date)}</span><strong>{prettyShortDate(day.date)}</strong></div><small>{formatHours(day.plannedMinutes)} / {formatHours(day.capacityMinutes)}</small></div>
      <Progress value={day.capacityMinutes ? day.plannedMinutes / day.capacityMinutes * 100 : 0} compact />
      <div className="day-allocations">{day.allocations.length === 0 ? <span>{day.capacityMinutes === 0 ? "Rest / no capacity" : "Available capacity"}</span> : day.allocations.map((allocation) => <button key={`${day.date}-${allocation.taskId}`} onClick={() => onEdit(allocation.taskId)}><Kind kind={allocation.kind} /><span>{allocation.taskTitle}</span><small>{formatHours(allocation.allocationMinutes)}</small></button>)}</div>
    </article>)}</div>
  </section>;
}

function SprintLearningPlans({ sprint }) {
  const learningTasks = sprint.tasks.filter((task) => task.kind === "learn");
  if (learningTasks.length === 0) return null;

  return <section className="sprint-learning-plans" aria-label={`Week ${sprint.weekNumber} learning plans`}>
    <header><p className="eyebrow">Learning assignments</p><h3>Learning plans by task</h3></header>
    <div className="learning-task-list">
      {learningTasks.map((task) => <TaskLearningPlan key={task.id} task={task} heading={task.title} headingAs="h4" assignmentHeadingAs="h5" showDescription />)}
    </div>
  </section>;
}

function Resources({ sprints, onOpenSprint }) {
  const [query, setQuery] = useState("");
  const term = query.trim().toLowerCase();
  const learningPlans = sprints.flatMap((sprint) => sprint.tasks
    .filter((task) => task.kind === "learn")
    .map((task) => {
      const resources = resourcesForTask(task);
      const taskMatches = searchableText(sprint.weekNumber, sprint.title, task.title, task.description).includes(term);
      return {
        sprint,
        task,
        resources: !term || taskMatches ? resources : resources.filter((resource) => searchableResource(resource).includes(term)),
      };
    }))
    .filter(({ sprint, task, resources }) => !term || resources.length > 0 || searchableText(sprint.weekNumber, sprint.title, task.title, task.description).includes(term));
  const resourceCount = learningPlans.reduce((total, plan) => total + plan.resources.length, 0);

  return <section className="page-stack">
    <article className="panel search-panel"><div><p className="eyebrow">Free and public only</p><h2>{resourceCount} learning assignments across {learningPlans.length} tasks</h2></div><label className="search-box"><span>Search learning plans</span><input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="C#, Identity, GKE, privacy…" /></label></article>
    {learningPlans.length === 0 ? <Empty title="No learning plans found" text="Try a task, provider, topic, or evidence type." /> :
      <div className="resource-task-groups">{learningPlans.map(({ sprint, task, resources }) => <article className="resource-task-group" key={task.id}>
        <header className="resource-task-heading">
          <div><span className="resource-week">Week {sprint.weekNumber} · {sprint.title}</span><h3>{task.title}</h3><p>{task.description}</p></div>
          <button className="secondary-button" onClick={() => onOpenSprint(sprint.id)}>View Week {sprint.weekNumber} plan →</button>
        </header>
        {resources.length === 0 ? <p className="learning-plan-empty">No matching assignments for this task.</p> :
          <div className="learning-assignment-list">{resources.map((resource) => <LearningAssignment key={resource.id} resource={resource} />)}</div>}
      </article>)}</div>}
  </section>;
}

function TaskLearningPlan({ task, heading, headingAs = "h3", assignmentHeadingAs = "h4", showDescription = false }) {
  if (task.kind !== "learn") return null;
  const Heading = headingAs;
  const resources = resourcesForTask(task);

  return <section className="task-learning-plan" aria-label={`${task.title} learning plan`}>
    <header className="task-learning-plan-heading">
      <p className="eyebrow">Learn task</p>
      <Heading>{heading}</Heading>
      {showDescription ? <p>{task.description}</p> : null}
    </header>
    {resources.length === 0 ? <p className="learning-plan-empty">No learning assignments are available for this task yet.</p> :
      <div className="learning-assignment-list">{resources.map((resource) => <LearningAssignment key={resource.id} resource={resource} headingAs={assignmentHeadingAs} />)}</div>}
  </section>;
}

function LearningAssignment({ resource, headingAs = "h4" }) {
  const Heading = headingAs;
  return <article className="learning-assignment">
    <header>
      <div className="assignment-badges"><span className={resource.required ? "required" : "optional"}>{resource.required ? "Required" : "Optional"}</span><span>{resource.estimatedMinutes} min</span></div>
      <Heading>{resource.title}</Heading>
      <p className="assignment-source">{resource.provider} · {resource.kind}</p>
    </header>
    <dl className="assignment-instructions">
      <div><dt>Read</dt><dd>{resource.readScope}</dd></div>
      <div><dt>Do</dt><dd>{resource.doInstruction}</dd></div>
      <div><dt>Evidence</dt><dd>{resource.evidence}</dd></div>
    </dl>
    <p className="assignment-note"><strong>Notes / caveat</strong>{resource.notes || "No additional caveats."}</p>
    <footer className="assignment-footer">
      <a href={resource.url} target="_blank" rel="noreferrer" aria-label={`Open ${resource.title} on ${resource.provider} in a new tab`}>Open exact resource ↗</a>
      {resource.lastVerifiedAt ? <small>Verified {prettyDate(resource.lastVerifiedAt)}{resource.reverifyOn ? ` · recheck ${prettyDate(resource.reverifyOn)}` : ""}</small> : null}
    </footer>
  </article>;
}

function RorayReading() {
  const entries = parseReadingEntries(readingStatusMarkdown);
  return <section className="page-stack">
    <article className="panel overview-strip">
      <div><p className="eyebrow">Manual reading log</p><h2>{entries.length} saved {entries.length === 1 ? "link" : "links"}</h2></div>
      <p>Maintained in <code>roray-status.md</code>. Links open in a new tab.</p>
    </article>
    <article className="panel reading-panel">
      {entries.length === 0 ? <Empty title="No reading links found" text="Add rows under Links/Articles in roray-status.md." /> :
        <div className="reading-table-wrap">
          <table className="reading-table">
            <caption className="sr-only">Roray reading links and completion status</caption>
            <thead><tr><th scope="col">Link</th><th scope="col">Status</th><th scope="col">Date</th></tr></thead>
            <tbody>{entries.map((entry, index) => <tr key={`${entry.href ?? entry.label}-${index}`}>
              <td>{entry.href ? <a href={entry.href} target="_blank" rel="noreferrer">{entry.label}<span aria-hidden="true"> ↗</span></a> : entry.label}</td>
              <td><span className={`reading-status ${statusClass(entry.status)}`}>{entry.status}</span></td>
              <td>{entry.date}</td>
            </tr>)}</tbody>
          </table>
        </div>}
    </article>
  </section>;
}

function Settings({ data, mutate }) {
  const tomorrow = addDays(data.today, 1);
  const [date, setDate] = useState(tomorrow);
  const [hours, setHours] = useState(0);
  const [reason, setReason] = useState("Day off");
  const [fromDate, setFromDate] = useState(data.today);
  return <section className="settings-grid">
    <article className="panel settings-card"><p className="eyebrow">Schedule override</p><h2>Add capacity or a day off</h2><p>Set a date to 0 hours for leave, or increase/decrease capacity. Future work is replanned immediately.</p>
      <form onSubmit={(e) => { e.preventDefault(); mutate(() => api.saveCapacity(date, Math.round(Number(hours) * 60), reason), "Capacity updated"); }}>
        <label>Date<input type="date" value={date} onChange={(e) => setDate(e.target.value)} required /></label>
        <label>Available hours<input type="number" min="0" max="24" step="0.25" value={hours} onChange={(e) => setHours(e.target.value)} required /></label>
        <label>Reason<input value={reason} onChange={(e) => setReason(e.target.value)} /></label>
        <button className="primary-button">Save override</button>
      </form>
      <div className="override-list">{data.capacityOverrides.map((override) => <div key={override.date}><span><strong>{prettyDate(override.date)}</strong><small>{formatHours(override.minutes)} · {override.reason || "Override"}</small></span><button onClick={() => mutate(() => api.deleteCapacity(override.date), "Override removed")}>Remove</button></div>)}</div>
    </article>
    <article className="panel settings-card"><p className="eyebrow">Manual replan</p><h2>Rebuild future allocation</h2><p>This removes allocations on and after the selected date, then schedules unfinished effort in sequence. Completed status and past check-ins are untouched.</p>
      <form onSubmit={(e) => { e.preventDefault(); mutate(() => api.reschedule(fromDate), "Schedule rebuilt"); }}><label>Replan from<input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} required /></label><button className="primary-button">Rebuild schedule</button></form>
    </article>
    <article className="panel settings-card"><p className="eyebrow">Portable backups</p><h2>Export current progress</h2><p>Downloads include the curriculum and current completion state. Back up the SQLite volume separately before host maintenance.</p>
      <div className="button-row"><a className="secondary-button" href="/api/export/roadmap.md">Download Markdown</a><a className="secondary-button" href="/api/export/roadmap.json">Download JSON</a></div>
    </article>
    <article className="panel settings-card"><p className="eyebrow">Schedule rules</p><h2>Capacity profile</h2><dl><div><dt>{prettyShortDate(data.metadata.startDate)}–{prettyShortDate(addDays(data.metadata.startDate, 20))}</dt><dd>8h Monday–Saturday; Sunday off</dd></div><div><dt>From {prettyShortDate(addDays(data.metadata.startDate, 21))}</dt><dd>4h weekdays; 6h Saturday and Sunday</dd></div><div><dt>Timezone</dt><dd>{data.metadata.timezone}</dd></div><div><dt>Target</dt><dd>{prettyDate(data.metadata.targetDate)}</dd></div></dl></article>
  </section>;
}

function TaskEditor({ task, sprint, onOpenSprint, onClose, onSave }) {
  const [status, setStatus] = useState(task.status);
  const [progress, setProgress] = useState(task.progressPercent);
  const [actualHours, setActualHours] = useState(task.actualMinutes / 60);
  const [notes, setNotes] = useState(task.notes);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    const listener = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", listener);
    return () => document.removeEventListener("keydown", listener);
  }, [onClose]);
  async function submit(event) {
    event.preventDefault(); setSaving(true);
    try { await onSave({ status, progressPercent: status === "done" ? 100 : Number(progress), actualMinutes: Math.round(Number(actualHours) * 60), notes }); }
    finally { setSaving(false); }
  }
  return <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
    <section className="task-modal" role="dialog" aria-modal="true" aria-labelledby="task-editor-title" aria-describedby="task-editor-description">
      <button autoFocus className="modal-close" onClick={onClose} aria-label="Close task">×</button><Kind kind={task.kind} />
      <h2 id="task-editor-title">{task.title}</h2><p id="task-editor-description">{task.description}</p>
      <div className="task-facts"><span>{formatHours(task.estimatedMinutes)} estimated</span><span>{task.notBefore}{task.notBefore === task.targetDate ? "" : ` → ${task.targetDate}`}</span></div>
      <TaskLearningPlan task={task} heading="Learning plan for this task" />
      {sprint && <section className="task-sprint-context" aria-label={`Week ${sprint.weekNumber} context`}>
        <div><p className="eyebrow">Week {sprint.weekNumber}</p><h3>{sprint.title}</h3></div>
        <button type="button" className="secondary-button" onClick={() => onOpenSprint(sprint.id)}>View Week {sprint.weekNumber} plan · {sprint.tasks.length} tasks · {learningAssignmentCount(sprint)} learning assignments</button>
      </section>}
      <form onSubmit={submit}>
        <label>Status<select value={status} onChange={(e) => setStatus(e.target.value)}><option value="todo">To do</option><option value="in_progress">In progress</option><option value="blocked">Blocked</option><option value="done">Done</option><option value="skipped">Skipped</option></select></label>
        <label>Progress: {status === "done" ? 100 : progress}%<input type="range" min="0" max="100" step="5" value={status === "done" ? 100 : progress} disabled={status === "done"} onChange={(e) => setProgress(e.target.value)} /></label>
        <label>Actual hours<input type="number" min="0" max="100" step="0.25" value={actualHours} onChange={(e) => setActualHours(e.target.value)} /></label>
        <label>Notes / evidence<textarea rows="5" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Commit, test result, ADR, insight, or blocker…" /></label>
        <div className="button-row"><button type="button" className="secondary-button" onClick={onClose}>Cancel</button><button className="primary-button" disabled={saving}>{saving ? "Saving…" : "Save progress"}</button></div>
      </form>
    </section>
  </div>;
}

function Login({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event) {
    event.preventDefault(); setBusy(true); setError("");
    try { await api.login(password); await onLogin(); }
    catch (reason) { setError(reason.message); }
    finally { setBusy(false); }
  }
  return <main className="login-page"><section className="login-card"><div className="brand-mark large">N</div><p className="eyebrow">Private learning workspace</p><h1>.NET ascent</h1><p>Sign in to your 624-hour architecture-first roadmap.</p><form onSubmit={submit}><label>Password<input autoFocus type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required /></label>{error && <p className="form-error">{error}</p>}<button className="primary-button" disabled={busy}>{busy ? "Signing in…" : "Sign in"}</button></form><small>Local default: change-me. Set secure environment values before cloud hosting.</small></section></main>;
}

function Stat({ label, value, detail }) { return <article className="stat-card"><span>{label}</span><strong>{value}</strong><small>{detail}</small></article>; }
function Progress({ value = 0, compact = false }) { return <span className={`progress ${compact ? "compact" : ""}`}><i style={{ width: `${Math.max(0, Math.min(100, value))}%` }} /></span>; }
function Kind({ kind }) { return <span className={`kind kind-${kind}`}>{kind}</span>; }
function Empty({ title, text }) { return <div className="empty-state"><strong>{title}</strong><span>{text}</span></div>; }
function Loading() { return <main className="loading-page"><span className="loader" /><p>Loading roadmap…</p></main>; }
function ErrorState({ onRetry }) { return <main className="loading-page"><h1>Roadmap unavailable</h1><button className="primary-button" onClick={onRetry}>Retry</button></main>; }

function sprintProgress(sprint) {
  if (!sprint?.tasks?.length) return 0;
  const total = sprint.tasks.reduce((sum, task) => sum + task.estimatedMinutes, 0);
  return sprint.tasks.reduce((sum, task) => sum + task.estimatedMinutes * task.progressPercent / 100, 0) / total * 100;
}
function resourcesForTask(task) { return (task.resources ?? []).filter((resource) => resource.taskId === task.id); }
function learningAssignmentCount(sprint) { return sprint.tasks.reduce((total, task) => total + (task.kind === "learn" ? resourcesForTask(task).length : 0), 0); }
function searchableText(...values) { return values.filter((value) => value !== undefined && value !== null).join(" ").toLowerCase(); }
function searchableResource(resource) { return searchableText(resource.title, resource.provider, resource.kind, resource.required ? "required" : "optional", `${resource.estimatedMinutes} minutes`, resource.readScope, resource.doInstruction, resource.evidence, resource.notes, resource.url, resource.lastVerifiedAt, resource.reverifyOn); }
function parseReadingEntries(markdown) {
  const lines = markdown.split(/\r?\n/);
  const headerIndex = lines.findIndex((line) => /^\|\s*link\s*\|\s*status\s*\|\s*date\s*\|$/i.test(line.trim()));
  if (headerIndex < 0) return [];
  const entries = [];
  for (const rawLine of lines.slice(headerIndex + 2)) {
    const line = rawLine.trim();
    if (!line.startsWith("|")) break;
    const cells = line.slice(1, line.endsWith("|") ? -1 : undefined).split("|").map((cell) => cell.trim());
    if (cells.length < 3 || !cells[0]) continue;
    const match = cells[0].match(/^\[([^\]]+)\]\((https?:\/\/.+)\)$/i);
    const label = match?.[1] ?? cells[0].replace(/^<|>$/g, "");
    const href = match?.[2] ?? label;
    entries.push({ label, href: /^https?:\/\//i.test(href) ? href : null, status: cells[1] || "—", date: cells[2] || "—" });
  }
  return entries;
}
function statusClass(status) { return status.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""); }
function formatHours(minutes = 0) { const value = minutes / 60; return `${Number.isInteger(value) ? value : value.toFixed(1)}h`; }
function formatPercent(value = 0) { return `${Math.round(value)}%`; }
function parseDate(value) { return new Date(`${value}T00:00:00`); }
function prettyDate(value) { return parseDate(value).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
function prettyShortDate(value) { return parseDate(value).toLocaleDateString("en-IN", { day: "numeric", month: "short" }); }
function weekday(value) { return parseDate(value).toLocaleDateString("en-IN", { weekday: "short" }); }
function addDays(value, days) { const date = parseDate(value); date.setDate(date.getDate() + days); return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`; }
